<template>
  <div v-if="searchingForFeatureFlags || isUsingOnlyAuth0" class="loading-screen">
    <div class="loading-box">
      <b-spinner class="orange-spinner" variant="warning" style="width: 5rem; height: 5rem;"></b-spinner>
    </div>
  </div>
  <div v-else id="homeDiv">
    <b-row align-h="center" class="h-100 m-0" align-v="center">
      <b-col class="d-flex justify-content-center">
        <b-card class="card-shadow login-cards" v-if="expiredLinkError">
          <b-row>
            <b-col :class="step == 'email' ? 'invisible' : null">
              <span class="hoverPointer" @click="back()" data-test-hook="cancel-button-expiredLink-home">
                <i class="fas fa-chevron-left"></i> {{ $t('xCancel') }}
              </span>
            </b-col>
          </b-row>
          <b-row>
            <b-col class="col-10">
              <b-img
                :src="require('@/assets/images/MobileLink_Logo_2023_COLOR.svg')"
                fluid
                alt="Mobile Link Logo"
              ></b-img>
            </b-col>
          </b-row>
          <b-row no-gutters class="mx-0 my-2 p-2">
            <b-col>
              {{ $t('xThisLinkHasExpired') }}
            </b-col>
          </b-row>
        </b-card>
        <b-card class="card-shadow login-cards" v-else-if="userEmailStatus == null">
          <b-row>
            <b-col :class="step == 'email' ? 'invisible' : null">
              <span class="hoverPointer" @click="back()" data-test-hook="cancel-button-noUserEmailStatus-home">
                <i class="fas fa-chevron-left"></i> {{ $t('xCancel') }}
              </span>
            </b-col>
          </b-row>
          <b-row>
            <b-col class="col-10">
              <b-img
                :src="require('@/assets/images/MobileLink_Logo_2023_COLOR.svg')"
                fluid
                alt="Mobile Link Logo"
              ></b-img>
            </b-col>
          </b-row>
          <b-row
            align-v="center"
            no-gutters
            class="errorBanner mx-0 my-2 p-2"
            v-if="loginError || showLoginErrorBanner"
          >
            <b-col>
              {{ $t('xAnErrorHasOccurredCheckEmail') }}
            </b-col>
          </b-row>
          <b-row align-v="center" no-gutters class="errorBanner mx-0 my-2 p-2" v-if="inviteError">
            <b-col>
              {{ $t('xItSeemsAsThoughYouHave') }}
            </b-col>
          </b-row>
          <b-row align-v="center" no-gutters class="errorBanner mx-0 my-2 p-2" v-if="correlationError">
            <b-col>
              {{ $t('xMobileLinkExperiencedAnErrorSigning') }}
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <b-alert :show="showValidationBanner" variant="danger" data-test-hook="ValidationAlert">
                {{ emailVerificationFailureMessage }}
              </b-alert>
            </b-col>
          </b-row>
          <b-row v-if="step == 'email'">
            <b-col>
              <h2>{{ $t('xSignInOrSignUp') }}</h2>
              <b-form-group id="email-group" label-for="email" :label="$t('xEmailAddress')">
                <b-form-input
                  id="email"
                  v-model="$v.email.$model"
                  maxlength="70"
                  type="text"
                  :placeholder="$t('xEmailAddress')"
                  :state="validState($v.email)"
                  aria-describedby="email-invalid"
                  :disabled="processing"
                  v-on:keyup.enter="next"
                  :formatter="lowerCaseFormatter"
                ></b-form-input>
                <b-form-invalid-feedback id="email-invalid">
                  {{ $t('xRequiredField') }}. {{ $t('xEmailInvalidError') }}
                </b-form-invalid-feedback>
                <span class="hoverPointer btn-link d-block" @click="$bvModal.show('ForgotEmailModal')">
                  {{ $t('xForgotYourEmail') }}
                </span>
              </b-form-group>
            </b-col>
          </b-row>
          <div v-if="step != 'verify'">
            <b-row class="mt-1 mb-3">
              <b-col>
                <b-button
                  block
                  variant="warning"
                  @click="next()"
                  :disabled="processing"
                  data-test-hook="next-button-noUserEmailStatus-home"
                >
                  {{ $t('xNext') }}
                  <b-spinner class="ml-2" v-if="processing"></b-spinner>
                  <span class="pl-1">
                    <i class="far fa-arrow-right"></i>
                  </span>
                </b-button>
              </b-col>
            </b-row>
          </div>
        </b-card>
        <b-card class="card-shadow login-cards" v-else>
          <b-row>
            <b-col :class="step == 'email' ? 'invisible' : null">
              <span class="hoverPointer" @click="back()" data-test-hook="cancel-button-else-home">
                <i class="fas fa-chevron-left"></i> {{ $t('xCancel') }}
              </span>
            </b-col>
          </b-row>
          <b-row class="my-3">
            <b-col>
              <b-img
                :src="require('@/assets/images/MobileLink_Logo_2023_COLOR.svg')"
                fluid
                alt="Mobile Link Logo"
              ></b-img>
            </b-col>
          </b-row>
          <b-row align-v="center" no-gutters class="mx-0 my-2 p-2" v-if="userEmailStatus == 3">
            <b-col>
              {{ $t('xYourEmailAddressHasAlreadyBeenInvited') }}
            </b-col>
          </b-row>
          <b-row align-v="center" no-gutters class="mx-0 my-2 p-2" v-else-if="userEmailStatus == 5">
            <b-col>
              {{ $t('xItLooksLikeWeEncounteredAProblem') }}
            </b-col>
            <b-button
              class="mx-0 my-3 p-2"
              block
              variant="warning"
              @click="retryLogin()"
              data-test-hook="retry-button-else-home"
            >
              {{ $t('xRetry') }}
              <span class="float-right">
                <i class="far fa-arrow-right"></i>
              </span>
            </b-button>
          </b-row>
          <b-row align-v="center" no-gutters class="mx-0 my-2 p-2" v-else>
            <b-col>
              {{ $t('xThereIsAnIssueWithYourAccount') }}
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>

    <ForgotEmailModal />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { required, email } from 'vuelidate/lib/validators'
import { validationMixin } from 'vuelidate'
import ForgotEmailModal from '@/components/Home/ForgotEmailModal'
import { vueAppApiBaseAddress } from '@/utils/mixins'
import * as Constants from '@/constants'
import { useAuth0FeatureFlagEnabled, useAuth0AndB2CFeatureFlagEnabled } from '../utils/mixins'
import { MigrationStatusTypes } from '../constants'

export default {
  components: { ForgotEmailModal },
  mixins: [validationMixin],
  data() {
    return {
      email: '',
      password: '',
      step: 'email',
      processing: false,
      users: null,
      termsConditionsUrl: process.env.VUE_APP_TERMS_CONDITIONS_URL,
      eulaUrl: process.env.VUE_APP_EULA_URL,
      privacyPolicyUrl: process.env.VUE_APP_PRIVACY_POLICY_URL,
      userEmailStatus: null,
      emailVerificationFailureMessage: null,
      showValidationBanner: false,
      showLoginErrorBanner: false,
      searchingForFeatureFlags: true
    }
  },
  validations: {
    email: {
      required,
      email
    }
  },
  computed: {
    ...mapState({
      loginError: state => state.account.loginError,
      correlationError: state => state.account.correlationError,
      expiredLinkError: state => state.account.expiredLinkError,
      inviteError: state => state.account.inviteError,
      hasUnreadMessage: state => state.account.hasUnreadMessages,
      currentAppConfig: state => state.application.currentAppConfig
    }),
    isUsingOnlyAuth0() {
      return useAuth0FeatureFlagEnabled(this.currentAppConfig)
    }
  },
  watch: {
    isUsingOnlyAuth0(newVal) {
      if (newVal) {
        this.autoNavigateToAuth0Login()
      }
    }
  },
  methods: {
    async next() {
      window.localStorage.removeItem(Constants.ForceSignOutErrorLocalStorageKey)
      this.showValidationBanner = false
      this.emailVerificationFailureMessage = null
      if (this.step == 'email') {
        this.$v.email.$touch()
        if (!this.$v.$anyError) {
          window.localStorage.setItem(Constants.UserEmailLocalStorageKey, this.email)
          this.processing = true
          await this.navigateBasedOnAuthProvider()
          this.processing = false
        }
      }
    },
    retryLogin() {
      this.userEmailStatus = null
    },
    lowerCaseFormatter(value) {
      return value.toLowerCase()
    },
    /**Auth0 Navigation method -- Takes user to auth0 login page */
    navigateBasedOnAuth0(userProvidedEmail, destinationParam) {
      window.location.assign(
        vueAppApiBaseAddress() + `Auth/Auth0/SignIn?userEmail=${userProvidedEmail}${destinationParam}`
      )
    },
    /**B2C Navigation Method -- Takes user to B2C login or sign up page */
    navigateBasedOnB2C(userProvidedEmail, destinationParam) {
      return this.$store
        .dispatch('migrationStatus', this.email)
        .then(response => {
          if (response.data.status === MigrationStatusTypes.Legacy) {
            window.location.assign(
              vueAppApiBaseAddress() + `Auth/Migrate?email=${userProvidedEmail}${destinationParam}`
            )
          } else if (response.data.status === MigrationStatusTypes.New) {
            window.location.assign(vueAppApiBaseAddress() + `Auth/SignUp?email=${userProvidedEmail}${destinationParam}`)
          } else if (response.data.status === MigrationStatusTypes.Valid) {
            window.location.assign(vueAppApiBaseAddress() + `Auth/SignIn?email=${userProvidedEmail}${destinationParam}`)
          } else {
            // This is for AutoEnroll, Retry and Unknown cases
            this.userEmailStatus = response.data.status
          }
        })
        .catch(error => {
          if (error.response?.status === 503 || error.response?.data?.status === 503) {
            this.emailVerificationFailureMessage = this.$t('xBillingMaintenanceMessage')
            this.showValidationBanner = true
          } else {
            this.showLoginErrorBanner = true
          }
        })
    },
    /**B2C and Auth0 Migration Method --
     * Takes users with a B2C account to the B2C Sign in page, takes new or Auth0 users to the Auth0 page */
    navigateBasedOnB2CAndAuth0(userProvidedEmail, destinationParam) {
      return this.$store
        .dispatch('migrationStatus', this.email)
        .then(response => {
          if (response.data.status === MigrationStatusTypes.New) {
            window.location.assign(
              vueAppApiBaseAddress() + `Auth/Auth0/SignIn?userEmail=${userProvidedEmail}${destinationParam}`
            )
          } else if (response.data.status === MigrationStatusTypes.Valid) {
            window.location.assign(vueAppApiBaseAddress() + `Auth/SignIn?email=${userProvidedEmail}${destinationParam}`)
          } else {
            // This is for AutoEnroll, Retry and Unknown cases
            this.userEmailStatus = response.data.status
          }
        })
        .catch(error => {
          if (error.response?.status === 503 || error.response?.data?.status === 503) {
            this.emailVerificationFailureMessage = this.$t('xBillingMaintenanceMessage')
            this.showValidationBanner = true
          } else {
            this.showLoginErrorBanner = true
          }
        })
    },
    async navigateBasedOnAuthProvider() {
      const userProvidedEmail = encodeURIComponent(this.email)
      const destination = this.$route.query.destination || '/dashboard'
      const destinationParam = `&destination=${encodeURIComponent(destination)}`

      if (this.isUsingOnlyAuth0) {
        //Sign up and Sign in through Auth0
        this.navigateBasedOnAuth0(userProvidedEmail, destinationParam)
      } else if (useAuth0AndB2CFeatureFlagEnabled(this.currentAppConfig)) {
        // New accounts will be signed up/in with Auth0, old accounts will continue with B2C
        await this.navigateBasedOnB2CAndAuth0(userProvidedEmail, destinationParam)
      } else {
        //Sign up and Sign In through B2C
        await this.navigateBasedOnB2C(userProvidedEmail, destinationParam)
      }
    },
    autoNavigateToAuth0Login() {
      const destination = this.$route.query.destination || '/dashboard'
      const destinationParam = `&destination=${encodeURIComponent(destination)}`
      setTimeout(() => {
        //necessary to prevent grey flash when immediately navigating to auth0
        this.navigateBasedOnAuth0('', destinationParam)
      }, 500)
    }
  },
  created() {
    if (this.$route.query.singleSignOnEmail) {
      this.email = this.$route.query.singleSignOnEmail
      this.processing = true
    }
  },
  mounted() {
    if (this.isUsingOnlyAuth0) {
      this.autoNavigateToAuth0Login()
    }
    let signOutErrorMessage = window.localStorage.getItem(Constants.ForceSignOutErrorLocalStorageKey)
    window.localStorage.removeItem(Constants.ForceSignOutErrorLocalStorageKey)
    window.localStorage.removeItem(Constants.UserEmailLocalStorageKey)
    if (this.$route.query.error) {
      this.showLoginErrorBanner = true
    }
    if (signOutErrorMessage) {
      this.emailVerificationFailureMessage = signOutErrorMessage
      this.showValidationBanner = true
    }
    if (document.cookie.indexOf('MobileLinkClientCookie=') == -1 && !this.processing) {
      let loginInterval = window.setInterval(() => {
        if (document.cookie.indexOf('MobileLinkClientCookie=') != -1) {
          clearInterval(loginInterval)
          this.$store.dispatch('getUser')

          this.$store.dispatch('getNewAnnouncements')
          this.$store.dispatch('getCountrySubdivisions')
          if (!this.hasUnreadMessage) this.$store.dispatch('getHasUnreadMessages')
          this.$store.dispatch('getApplicationConfiguration', 'Any')
        }
      }, 3000)
    }
    this.$store.dispatch('getApplicationConfiguration', 'Any').finally(() => {
      this.searchingForFeatureFlags = false
    })
  }
}
</script>

<style scoped>
.loading-screen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: var(--blue-900);
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: auto;
}

@media (max-height: 700px) {
  .loading-screen {
    align-items: flex-start;
    padding-top: 75px;
  }
}

.loading-box {
  /* The width/height matches the auth0 login box */
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: center;
  align-items: center;
  width: 403px;
  height: 539px;
  flex-shrink: 0;
  padding-top: 300;
}

@media (max-width: 594px) {
  /* Used to fill fullscreen if window is too small*/
  .loading-box {
    width: 100%;
    height: 100%;
    border-radius: 0;
    max-width: none;
  }
}

.orange-spinner {
  color: #ff6f00 !important;
  border-color: #ff6f00 transparent transparent transparent !important;
}
h2 {
  margin: 0.5em 0 1em 0;
  font-weight: 500;
  font-size: 1.25em;
}
.btn-link {
  outline: none !important;
  box-shadow: none !important;
  text-decoration: underline;
  font-size: 1em;
  padding: 0.75em 0;
}
.btn-link:hover {
  color: var(--blue-900);
  background-color: transparent !important;
  text-decoration: underline;
}
.errorIcon {
  color: var(--red-500);
}
.login-cards {
  z-index: 100;
}
@media only screen and (max-width: 600px) {
  .login-cards {
    width: 100%;
  }
}

/* Small devices (portrait tablets and large phones, 600px and up) */
@media only screen and (min-width: 600px) {
  .login-cards {
    width: 414px;
  }
}

/* Medium devices (landscape tablets, 768px and up) */
@media only screen and (min-width: 768px) {
  .login-cards {
    width: 414px;
  }
}

.card-body {
  padding: 3rem 2rem;
}

#homeDiv {
  height: 100vh;
  color: var(--carbon-900);
  background: var(--blue-900);
  margin-left: -15px;
  margin-right: -15px;
}

#backgroundImage {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 75%;
  z-index: 1;
}
</style>
